/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import FontFaceObserver from 'fontfaceobserver';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import 'sanitize.css/sanitize.css';

// Import root app
import App from './components/pages/App';

import LanguageProvider from './components/pages/LanguageProvider';
import SomethingWentWrong from './components/pages/SomethingWentWrong';

/* eslint-disable import/no-unresolved, import/extensions */
import '!file-loader?name=[name].[ext]!./favicon.ico';
import 'file-loader?name=.htaccess!./.htaccess';
/* eslint-enable import/no-unresolved, import/extensions */

import { configureStore } from '@capillarytech/vulcan-react-sdk/utils';
import { initialReducer } from './initialReducer';
import history from './utils/history';
import initialState from './initialState';
import { translationMessages } from './i18n';
import { bugsnagDiagnosticData } from './utils/bugsnag';

const openSansObserver = new FontFaceObserver('Roboto', {});

// When Roboto is loaded, add a font-family using Robotot to the body
openSansObserver
  .load()
  .then(() => {
    document.body.classList.add('fontLoaded');
  })
  .catch(err => {
    Bugsnag.notify(err);
    console.log(err);
  });

// Create redux store with history
const store = configureStore(initialState, initialReducer, history);
const MOUNT_NODE = document.getElementById('badges-app');

const bugSnagErrorCallback = event => {  
  bugsnagDiagnosticData(event);
  if (window.FS && window.FS.getCurrentSessionURL) {
    event.addMetadata('fullstory', {
      sessionURL: window.FS.getCurrentSessionURL(true),
    });
  }
};

Bugsnag.start({
  // eslint-disable-next-line no-undef
  appVersion: BUGSNAG_APP_VERSION,
  // eslint-disable-next-line no-undef
  apiKey: BUGSNAG_API_KEY,
  releaseStage: window.location.hostname,
  plugins: [new BugsnagPluginReact()],
  onError: bugSnagErrorCallback,
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

export const BugsnagClient = Bugsnag;

const ErrorScreen = () => <SomethingWentWrong />;

const root = createRoot(MOUNT_NODE);
const render = messages => {
  root.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ErrorBoundary FallbackComponent={ErrorScreen}>
          <App />
        </ErrorBoundary>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE,
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'components/pages/App'], () => {
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise(resolve => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en')]))
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}
